/**
 * Fiscal Sale Dialog - Diálogo para finalizar venda com tipo fiscal
 * 
 * Permite escolher entre:
 * - Venda-a-Dinheiro (VD) - Pronto pagamento
 * - Factura (FAC) - Crédito
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  Banknote, CreditCard, AlertTriangle, CheckCircle, 
  FileText, Receipt, Calendar
} from 'lucide-react';
import { formatCurrency, formatDisplayCode, formatDisplayOnlyCode } from '@/lib/utils';
import { printAPI } from '@/lib/api';
import { getStoredAuthToken } from '@/lib/authStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FiscalSaleDialog({ 
  open, 
  onOpenChange, 
  sale,
  onComplete,
}) {
  const [paymentType, setPaymentType] = useState('cash'); // 'cash' ou 'credit'
  const [creditDays, setCreditDays] = useState(30);
  const [processing, setProcessing] = useState(false);
  const [fiscalResult, setFiscalResult] = useState(null);
  
  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setPaymentType('cash');
      setCreditDays(30);
      setFiscalResult(null);
    }
  }, [open]);
  
  // Calculate payment status
  const totalPaid = sale?.paid_amount ?? (sale?.payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0);
  const totalAmount = sale?.total_amount || 0;
  const changeAmount = sale?.change_amount || 0;
  const effectivePaid = Math.min(totalAmount, Math.max(totalPaid - changeAmount, 0));
  const remainingBalance = Math.max(totalAmount - effectivePaid, 0);
  const isPaidInFull = remainingBalance < 0.01;
  
  // Auto-select based on payment status
  useEffect(() => {
    if (sale && !isPaidInFull && remainingBalance > 0.009) {
      setPaymentType('credit');
    }
  }, [sale, isPaidInFull, remainingBalance]);
  
  if (!sale) return null;
  
  const handleEmitDocument = async () => {
    setProcessing(true);
    try {
      const token = getStoredAuthToken();
      
      if (paymentType === 'cash') {
        // Emitir Venda-a-Dinheiro
        const response = await fetch(`${API_URL}/api/fiscal/venda-dinheiro/${sale.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.detail || 'Erro ao emitir documento');
        }
        
        setFiscalResult({
          type: 'venda_dinheiro',
          number: result.fiscal_document_number,
          message: 'Venda-a-Dinheiro emitida com sucesso!'
        });
        
        toast.success(`Venda-a-Dinheiro ${formatDisplayOnlyCode(result.fiscal_document_number, 'VD')} emitida!`);
        
      } else {
        // Emitir Factura
        const initialPaidParam = effectivePaid > 0 ? `&initial_paid=${encodeURIComponent(effectivePaid.toFixed(2))}` : '';
        const response = await fetch(`${API_URL}/api/fiscal/factura/${sale.id}?credit_days=${creditDays}${initialPaidParam}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.detail || 'Erro ao emitir documento');
        }
        
        setFiscalResult({
          type: 'factura',
          number: result.fiscal_document_number,
          dueDate: result.due_date,
          balanceDue: result.balance_due,
          message: 'Factura emitida com sucesso!'
        });
        
        toast.success(`Factura ${formatDisplayOnlyCode(result.fiscal_document_number, 'FAC')} emitida!`);
      }
      
      if (onComplete) {
        onComplete();
      }
      
    } catch (error) {
      toast.error(error.message || 'Erro ao emitir documento fiscal');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleDownloadPDF = async (copyType = 'Original') => {
    if (!fiscalResult) return;

    const docType = fiscalResult.type === 'venda_dinheiro' ? 'cash_sale' : 'invoice';
    const result = await printAPI.renderDocumentPdf({
      doc_type: docType,
      sale_id: sale.id,
      location_id: sale.location_id,
      module_group: 'VENDAS',
      copy_type: copyType
    }, 'preview');

    if (!result.success) {
      toast.error(result.error || 'Erro ao gerar PDF');
    }
  };
  
  const handleClose = () => {
    onOpenChange(false);
    setFiscalResult(null);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="fiscal-sale-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Emitir Documento Fiscal
          </DialogTitle>
          <DialogDescription>
            Venda: {formatDisplayOnlyCode(sale.sale_number, 'SAL')}
          </DialogDescription>
        </DialogHeader>
        
        {/* Sale Summary */}
        <div className="bg-slate-50 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Total da Venda</span>
            <span className="font-bold text-lg">{formatCurrency(totalAmount)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Valor Recebido</span>
            <span className={isPaidInFull ? 'text-green-600' : 'text-amber-600'}>
              {formatCurrency(totalPaid)}
            </span>
          </div>
          {changeAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Troco</span>
              <span className="text-green-600 font-medium">
                {formatCurrency(changeAmount)}
              </span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Valor Aplicado</span>
            <span className={remainingBalance > 0.009 ? 'text-amber-600' : 'text-green-700'}>
              {formatCurrency(effectivePaid)}
            </span>
          </div>
          {remainingBalance > 0.009 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Saldo em Falta</span>
              <span className="text-red-600 font-medium">
                {formatCurrency(remainingBalance)}
              </span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Cliente</span>
            <span>{sale.customer_name || 'Cliente Indiferenciado'}</span>
          </div>
        </div>
        
        {fiscalResult ? (
          // Document issued - show result
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">{fiscalResult.message}</AlertTitle>
              <AlertDescription className="text-green-700">
                <p className="font-medium text-lg mt-1">{fiscalResult.number}</p>
                {fiscalResult.dueDate && (
                  <p className="text-sm mt-1">
                    Vencimento: {new Date(fiscalResult.dueDate).toLocaleDateString('pt-MZ')}
                  </p>
                )}
                {fiscalResult.balanceDue > 0 && (
                  <p className="text-sm">
                    Saldo em dívida: {formatCurrency(fiscalResult.balanceDue)}
                  </p>
                )}
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <p className="text-sm font-medium">Imprimir documento:</p>
              <div className="flex gap-2">
                <Button onClick={() => handleDownloadPDF('Original')} className="flex-1">
                  <FileText className="h-4 w-4 mr-2" />
                  Original
                </Button>
                <Button onClick={() => handleDownloadPDF('Duplicado')} variant="outline" className="flex-1">
                  <FileText className="h-4 w-4 mr-2" />
                  Duplicado
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // Select payment type
          <div className="space-y-4">
            <RadioGroup value={paymentType} onValueChange={setPaymentType}>
              {/* Venda-a-Dinheiro */}
              <div className={`
                border rounded-lg p-4 cursor-pointer transition-all
                ${paymentType === 'cash' ? 'border-green-500 bg-green-50' : 'border-gray-200'}
                ${!isPaidInFull ? 'opacity-50' : ''}
              `}>
                <div className="flex items-start gap-3">
                  <RadioGroupItem 
                    value="cash" 
                    id="cash" 
                    disabled={!isPaidInFull}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label htmlFor="cash" className="flex items-center gap-2 cursor-pointer">
                      <Banknote className="h-5 w-5 text-green-600" />
                      <span className="font-medium">Venda-a-Dinheiro (VD)</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        Pronto Pagamento
                      </Badge>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Documento para vendas pagas na totalidade no acto da compra. Não gera dívida.
                    </p>
                    {!isPaidInFull && (
                      <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        Pagamento incompleto - não disponível
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Factura */}
              <div className={`
                border rounded-lg p-4 cursor-pointer transition-all
                ${paymentType === 'credit' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
              `}>
                <div className="flex items-start gap-3">
                  <RadioGroupItem value="credit" id="credit" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="credit" className="flex items-center gap-2 cursor-pointer">
                      <CreditCard className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">Factura (FAC)</span>
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        Crédito
                      </Badge>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Documento para vendas a crédito. Gera conta a receber e requer Recibo para liquidar.
                    </p>
                    
                    {paymentType === 'credit' && (
                      <div className="mt-3 flex items-center gap-3">
                        <Label className="text-sm">Prazo:</Label>
                        <Select value={String(creditDays)} onValueChange={(v) => setCreditDays(parseInt(v))}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">7 dias</SelectItem>
                            <SelectItem value="15">15 dias</SelectItem>
                            <SelectItem value="30">30 dias</SelectItem>
                            <SelectItem value="45">45 dias</SelectItem>
                            <SelectItem value="60">60 dias</SelectItem>
                            <SelectItem value="90">90 dias</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          Vence: {new Date(Date.now() + creditDays * 24 * 60 * 60 * 1000).toLocaleDateString('pt-MZ')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </RadioGroup>
            
            {/* Warning for credit */}
            {paymentType === 'credit' && !sale.customer_name && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  Recomendado: Identifique o cliente para vendas a crédito.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
        
        <Separator />
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {fiscalResult ? 'Fechar' : 'Cancelar'}
          </Button>
          {!fiscalResult && (
            <Button 
              onClick={handleEmitDocument} 
              disabled={processing}
              data-testid="emit-fiscal-btn"
            >
              {processing ? 'A processar...' : 'Emitir Documento'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
